import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, Wallet, Megaphone, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { buildPlannerHref } from '../../utils/plannerLink';

/**
 * @param {{
 *   plannerCta?: object | null,
 *   sidebarTips: string[],
 *   sidebarWarnings: string[],
 *   ctaTitle?: string,
 * }} props
 */
export function GuideSidebar({ plannerCta, sidebarTips, sidebarWarnings, ctaTitle = 'Build your itinerary' }) {
  const href = plannerCta ? buildPlannerHref(plannerCta) : '/itinerary-planner';

  return (
    <div className="lg:sticky lg:top-24 space-y-8">
      <div className="rounded-3xl p-8 shadow-2xl relative overflow-hidden bg-on-surface text-white border border-outline-variant/20 dark:bg-slate-950 dark:border-slate-700">
        <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-primary rounded-full blur-3xl opacity-25 pointer-events-none" aria-hidden />
        <h3 className="text-xl sm:text-2xl font-bold font-headline leading-tight mb-3 relative">{ctaTitle}</h3>
        <p className="text-surface-variant dark:text-slate-400 text-sm mb-6 leading-relaxed relative">
          Set match day, budget, and transport—our AI fills day parts you can edit before you travel.
        </p>
        {plannerCta && (
          <div className="space-y-3 mb-6 relative text-sm">
            <div className="bg-white/10 p-3 rounded-xl border border-white/10 flex items-center gap-3">
              <CalendarRange className="h-5 w-5 text-primary-container shrink-0" aria-hidden />
              <span className="text-white/90">{plannerCta.days} days · match day {plannerCta.matchDay || '—'}</span>
            </div>
            <div className="bg-white/10 p-3 rounded-xl border border-white/10 flex items-center gap-3">
              <Wallet className="h-5 w-5 text-secondary-fixed shrink-0" aria-hidden />
              <span className="text-white/90 capitalize">{plannerCta.budget || 'medium'} budget</span>
            </div>
          </div>
        )}
        <Link
          to={href}
          className="relative block w-full text-center bg-primary-container text-on-primary-container py-4 rounded-xl font-black uppercase tracking-wider text-sm shadow-lg hover:brightness-105 active:scale-[0.99] transition-all"
        >
          Launch AI planner
        </Link>
      </div>

      {sidebarTips?.length > 0 && (
        <div className="rounded-3xl p-6 bg-surface-container-high dark:bg-slate-800/90 border border-outline-variant/15 dark:border-slate-700">
          <h4 className="font-bold text-lg mb-4 flex items-center gap-2 font-headline text-on-surface dark:text-white">
            <Megaphone className="h-5 w-5 text-primary shrink-0" aria-hidden />
            Planning tips
          </h4>
          <ul className="space-y-3">
            {sidebarTips.map((tip) => (
              <li key={tip} className="flex gap-3 items-start text-sm text-on-surface-variant dark:text-slate-300 font-medium leading-relaxed">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sidebarWarnings?.length > 0 && (
        <div className="rounded-3xl p-6 bg-tertiary/5 dark:bg-tertiary/10 border border-tertiary/15 dark:border-tertiary/30">
          <h4 className="font-bold text-lg mb-4 text-tertiary flex items-center gap-2 font-headline">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
            Avoid these mistakes
          </h4>
          <ul className="space-y-2.5">
            {sidebarWarnings.map((w) => (
              <li key={w} className="text-sm font-semibold text-on-surface dark:text-slate-200 leading-snug">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default GuideSidebar;
